import at from 'lodash/at';
import L from 'leaflet';
import { distanceBetween } from 'geofire-common';
import defaultPinIcon from './static/knicks-pin.svg';
import msgPinIcon from './static/knicks-ball-logo.png';
import { map } from './config';

/**
 * Calculates the duration of a video file.
 *
 * @param {File} file - The video file for which to calculate the duration.
 * @return {Promise<number>} A promise that resolves to the duration of the video in seconds.
 */
export const getVideoDuration = async (file) => {
  const url = URL.createObjectURL(file);
  const audio = new Audio();

  audio.muted = true;
  audio.src = url;
  audio.preload = 'metadata';

  return new Promise((resolve) => {
    audio.addEventListener('loadedmetadata', () => resolve(audio.duration));
  });
};

export const roundPretty = (num) => (Math.round(num * 100) / 100).toLocaleString();

export const getRandomIndex = (max) => Math.floor(Math.random() * max);

export const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

export const adjustViewportHeight = () => {
  // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

export const getClusterMarkers = (target) => {
  const mapMarker = (m) => ({ ...m.options, position: m.getLatLng() });
  const sortMarker = (_a, b) => (b.options.id === target.options.id ? 1 : -1);
  const filterMarker = (marker) => {
    const { lat: centerLat, lng: centerLng } = target.getLatLng();
    const distanceInKm = distanceBetween(
      [marker._latlng.lat, marker._latlng.lng],
      [centerLat, centerLng],
    );
    return distanceInKm <= map.maxClusterDistanceInKm;
  };

  const markers = target?.__parent
    ? target.__parent.getAllChildMarkers().filter(filterMarker).sort(sortMarker).map(mapMarker)
    : [{ ...target.options }];

  return markers;
};

export const getMarkerIcon = ({ name }) => {
  if (name === 'Madison Square Garden') {
    return L.icon({ iconUrl: msgPinIcon, iconSize: [65, 62] });
  }

  return L.icon({
    iconUrl: defaultPinIcon,
    iconSize: [45, 50],
  });
};

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export const getAddressShortName = (address) =>
  at(address, ['town', 'city', 'county', 'state', 'province', 'country'])
    .filter((v) => v && v.length > 0)
    .slice(0, 2)
    .join(', ');

export const formatGoogleLocationResult = (location) => {
  const { place_id, geometry, formatted_address, address_components } = location;

  const zipInfo = address_components.find((component) => component.types.includes('postal_code'));
  const zip = zipInfo ? zipInfo.long_name || zipInfo.short_name : '';

  const countryInfo = address_components.find((component) => component.types.includes('country'));
  const country = countryInfo ? countryInfo.long_name || countryInfo.short_name : '';

  const localityInfo = address_components.find((component) => component.types.includes('locality'));
  const locality = localityInfo ? localityInfo.long_name || localityInfo.short_name : '';

  const sublocalityInfo = address_components.find((component) =>
    component.types.includes('sublocality'),
  );
  const sublocality = sublocalityInfo
    ? sublocalityInfo.long_name || sublocalityInfo.short_name
    : '';

  const neighborhoodInfo = address_components.find((component) =>
    component.types.includes('neighborhood'),
  );
  const neighborhood = neighborhoodInfo
    ? neighborhoodInfo.long_name || neighborhoodInfo.short_name
    : '';

  const routeInfo = address_components.find((component) => component.types.includes('route'));
  const route = routeInfo ? routeInfo.long_name || routeInfo.short_name : '';

  const formattedLocation = {
    place_id,
    formatted_address,
    zip,
    country,
    locality,
    sublocality,
    neighborhood,
    route,
    latitude: geometry.location.lat,
    longitude: geometry.location.lng,
    placeLat: geometry.location.lat,
    placeLng: geometry.location.lng,
  };

  return formattedLocation;
};

export const parseLocationAreaName = (location) => {
  const availableNames = at(location, [
    'neighborhood',
    'locality',
    'sublocality',
    'zip',
    'country',
  ]).filter((v) => v && v.length > 0);

  return availableNames[0];
};
